/**
 * Utilidades para hacer clickeables las citas `[Fuente N]` que el LLM
 * incrusta en las respuestas del chat.
 *
 * Las respuestas del asistente vienen con marcas literales tipo
 * "[Fuente 1]", "[Fuente 4]" que se corresponden con el orden en que
 * el RAG suministró los chunks al LLM. Sin tratamiento, el usuario
 * ve esas marcas como texto plano sin saber a qué documento se
 * refieren.
 *
 * Estrategia:
 *   - `linkifyCitations(text, messageId)` pre-procesa el markdown y
 *     transforma `[Fuente N]` en `[Fuente N](#source-N-<msg>)`. Así
 *     React-Markdown lo renderiza como link y podemos darle estilo de
 *     chip clickeable vía `components.a`.
 *   - `handleCitationClick` intercepta el click, hace scroll suave a
 *     la fuente correspondiente y aplica un flash visual (ring teal)
 *     para que el usuario localice exactamente cuál es.
 *   - `sourceAnchorId(n, messageId)` produce el id consistente que se
 *     pone en la lista de fuentes (id="source-N-<msg>") y se referencia
 *     desde los chips.
 */
import type { MouseEvent } from "react";

export function linkifyCitations(content: string, messageId: string): string {
  // Tolera variaciones de espaciado: "[Fuente 4]", "[Fuente  4]", etc.
  return content.replace(
    /\[Fuente\s+(\d+)\]/g,
    (_, n) => `[Fuente ${n}](#source-${n}-${messageId})`
  );
}

export function sourceAnchorId(num: number, messageId: string): string {
  return `source-${num}-${messageId}`;
}

const HIGHLIGHT_CLASSES = ["ring-2", "ring-brand", "ring-offset-2"];

export function handleCitationClick(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href") || "";
  if (!href.startsWith("#source-")) return;
  e.preventDefault();
  const target = document.getElementById(href.slice(1));
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  target.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => {
    target.classList.remove(...HIGHLIGHT_CLASSES);
  }, 1600);
}
